import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { openSecureNewTab } from '../utils';
import { Environment, EnvironmentService } from '@galaxy/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  BOOOKING_URL_DEMO,
  BOOOKING_URL_PROD,
  MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$,
  MS_CONTEXT,
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
} from '../constants';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  CONTACT_PROFILE_PAGE_TOKEN,
  CONTEXT_DATA_TOKEN,
  FEATURE_TEAM_MEMBER_FILTER_TOKEN,
  USER_ID_TOKEN,
} from '../data-providers/providers';
import {
  AttachmentInterface,
  Attendee,
  Calendar,
  HostUser,
  Meeting,
  MeetingFormAnswer,
  MeetingType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  GroupMeetingType,
  isGroupMeetingType,
  MeetingSchedulerStoreService,
} from '../data-providers/meeting-scheduler-store.service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { AttendeeListComponent } from '../shared/components/attendee-list/attendee-list.component';
import { MatDialog } from '@angular/material/dialog';
import {
  TeamMemberFilterDialogComponent,
  TeamMemberFilterDialogData,
} from './team-member-filter-dialog/team-member-filter-dialog.component';

export interface CalenderViewDataSource {
  meetingId: string;
  title: string;
  joinMeeting: string;
  date: { start: Date; end: Date };
  guest: string;
  contact: { email: string; phoneNumber: string };
  primaryContact: { name: string; contactId: string };
  attendeeEmails: string;
  comments: string;
  location: string;
  created: Date;
  formAnswers: MeetingFormAnswer[];
  attachments: AttachmentInterface[];
  calendarId: string;
  meetingType: TeamEventMeetingType;
  hostNames: string[];
  hostUserIds?: string[];
  topic?: string;
  isSessionEventType: boolean;
  eventTypeId?: string;
  // Session-specific properties
  sessionTitle?: string;
  sessionRegistrationUrl?: string;
  registeredAttendeeCount: string;
  maxAttendeeCount?: string;
  registrationCutoff?: string;
  attendees?: Attendee[];
}

interface TimeSpan {
  start: Date;
  end: Date;
}

interface LoadMeetingFilters {
  meetingTypeIds: string[];
  timeSpan: TimeSpan;
  userIds?: string[];
}

@Component({
  selector: 'meeting-scheduler-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalenderViewComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly environmentService = inject(EnvironmentService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly featureTeamMemberFilterEnabled$ = inject(FEATURE_TEAM_MEMBER_FILTER_TOKEN);
  private readonly contextData$ = inject(CONTEXT_DATA_TOKEN);
  private businessId = '';

  private meetings$$: BehaviorSubject<Meeting[]> = new BehaviorSubject<Meeting[]>([]);
  meetings$: Observable<Meeting[]> = this.meetings$$.asObservable();
  dataSource$: Observable<CalenderViewDataSource[]>;
  allMeetingTypes$: Observable<MeetingType[]>;
  personalEventTypeMeetings$: Observable<Calendar>;
  personalEventTypes$: Observable<MeetingType[]>;
  teamEventTypeMeetings$: Observable<{ [calendarId: string]: GroupMeetingType[] }>;
  teamEventTypes$: Observable<MeetingType[]> = of(null);

  // to identify new Date meeting
  previousDate = '';

  hideLoader$ = new BehaviorSubject<boolean>(false);

  eventTypeFilter$$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  meetingTimeSpanFilter$$: BehaviorSubject<TimeSpan> = new BehaviorSubject({
    start: new Date(),
    end: this.getTenYearsLater(),
  });
  updatedAttendeeCount$$: BehaviorSubject<{ meetingId: string; count: number } | null> = new BehaviorSubject(null);
  selectedTab: 'upcoming' | 'past' = 'upcoming';
  now = new Date();
  selectedEventTypeFilter: string;
  timeRangeKeySelected = '90days';

  isBusinessCenter = false;
  showTeamMemberFilter$: Observable<boolean>;
  filtersReady$ = new BehaviorSubject<boolean>(false);
  teamMembers$: Observable<HostUser[]>;
  selectedUserLabel$: Observable<string>;

  private allMeetings: Meeting[] = [];
  hasMore = false;
  nextCursor = '';
  isLoadingMore = false;
  isInitialLoad = true;
  private paginationSubscription: Subscription | null = null;
  timeRangeOptions = [
    {
      key: '7days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.7_DAYS'),
      dateRange: [moment(this.now).subtract(7, 'days').toDate(), this.now],
    },
    {
      key: '30days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.30_DAYS'),
      dateRange: [moment(this.now).subtract(30, 'days').toDate(), this.now],
    },
    {
      key: '90days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.90_DAYS'),
      dateRange: [moment(this.now).subtract(90, 'days').toDate(), this.now],
    },
    {
      key: '6months',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.6_MONTHS'),
      dateRange: [moment(this.now).subtract(6, 'months').toDate(), this.now],
    },
    {
      key: '12months',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.12_MONTHS'),
      dateRange: [moment(this.now).subtract(12, 'months').toDate(), this.now],
    },
    {
      key: 'custom',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.CUSTOM'),
      dateRange: [moment(this.now).subtract(90, 'days').toDate(), this.now],
    },
  ];
  private subscriptions: Subscription[] = [];
  contactProfilePageLink = '';

  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly translate: TranslateService,
    @Inject(MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$) protected context: MS_CONTEXT,
    @Inject(USER_ID_TOKEN) private readonly userId$: Observable<string>,
    private readonly analyticsService: ProductAnalyticsService,
    @Inject(CONTACT_PROFILE_PAGE_TOKEN) private readonly contactProfilePageLink$: Observable<string>,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.isBusinessCenter = this.context === MS_CONTEXT.MS_CONTEXT_SMB;
    this.showTeamMemberFilter$ = this.isBusinessCenter
      ? this.featureTeamMemberFilterEnabled$.pipe(startWith(false), shareReplay(1))
      : of(false);

    this.subscriptions.push(this.contactProfilePageLink$.subscribe((value) => (this.contactProfilePageLink = value)));
    this.subscriptions.push(
      this.contextData$.pipe(first()).subscribe((data) => {
        this.businessId = data.namespace;
      }),
    );

    this.meetingSchedulerStoreService
      .loadHasHostEverBeenConfigured()
      .pipe(take(1))
      .subscribe((hasHostEverBeenConfigured) => {
        if (hasHostEverBeenConfigured)
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.VIEW_MY_MEETINGS,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
      });

    if (this.isBusinessCenter) {
      this.loadEventTypes();

      this.subscriptions.push(
        this.featureTeamMemberFilterEnabled$.pipe(first()).subscribe((enabled) => {
          if (enabled) {
            this.teamMembers$ = this.meetingSchedulerStoreService.loadGroupCalendars().pipe(
              filter((calendars) => calendars.length > 0),
              first(),
              switchMap((calendars) => this.meetingSchedulerStoreService.loadHostsForCalendar(calendars[0].id)),
              map((response) => response.hostUsers),
              shareReplay(1),
            );

            this.selectedUserLabel$ = combineLatest([
              this.teamMembers$,
              this.meetingSchedulerStoreService.getSelectedUserIds$(),
            ]).pipe(
              map(([members, selectedIds]) => {
                const prefix = this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.USERS_LABEL');
                if (selectedIds.length === 0) {
                  return `${prefix}: ${this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.NONE')}`;
                }
                const names = members.filter((m) => selectedIds.includes(m.userId)).map((m) => m.displayName);
                return `${prefix}: ${names.join(', ')}`;
              }),
            );

            this.teamMembers$.pipe(first()).subscribe({
              next: () => {
                this.filtersReady$.next(true);
                this.cdr.markForCheck();
              },
              error: () => {
                this.filtersReady$.next(true);
                this.cdr.markForCheck();
              },
            });
          } else {
            this.filtersReady$.next(true);
          }
        }),
      );
    } else {
      this.filtersReady$.next(true);
      this.loadEventTypes();
    }

    this.personalEventTypeMeetings$ = this.meetingSchedulerStoreService.loadPersonalCalendar();

    const loadMeetingFilters$ = this.setupLoadMeetingFilters();
    this.subscriptions.push(
      loadMeetingFilters$
        .pipe(
          switchMap((filters) => {
            this.allMeetings = [];
            this.nextCursor = '';
            this.hasMore = false;
            this.isInitialLoad = true;
            this.hideLoader$.next(false);
            return this.loadMeetings(filters, '');
          }),
        )
        .subscribe({
          next: (response) => {
            this.allMeetings = response.meetings;
            this.hasMore = response.hasMore;
            this.nextCursor = response.cursor;
            this.isInitialLoad = false;
            this.hideLoader$.next(true);
            this.meetings$$.next(response.meetings);
          },
          error: () => {
            this.isInitialLoad = false;
            this.hideLoader$.next(true);
            this.meetings$$.next([]);
          },
        }),
    );

    const sessionCalendarId$ = this.meetingSchedulerStoreService.loadSessionCalendar().pipe(
      map((sessionCalendar) => sessionCalendar?.id),
      startWith(null),
      shareReplay(1),
    );

    this.dataSource$ = combineLatest([this.meetings$, sessionCalendarId$, this.updatedAttendeeCount$$]).pipe(
      map(([meetings, sessionCalendarId, updatedAttendeeCount]) =>
        meetings
          .filter((m) => m.id)
          .map((m) => {
            const isSessionEvent = sessionCalendarId && m.calendarId === sessionCalendarId;
            const attendees = m.attendees || [];
            const primaryAttendee = attendees.find((a) => a.isPrimary) || {
              email: '',
              phoneNumber: '',
              firstName: '',
              lastName: '',
              contactId: '',
            };
            const hostDetails = m?.hostDetails || [];

            const baseItem: CalenderViewDataSource = {
              meetingId: m.id!,
              title: m.topic || '',
              eventTypeId: m.eventTypeId,
              joinMeeting: m.joinMeetingUrl.replace('https', 'http') || '',
              date: { start: m.start, end: m.end },
              guest: attendees[0] ? `${attendees[0].firstName} ${attendees[0].lastName}` : '',
              contact: primaryAttendee,
              primaryContact: {
                name: `${primaryAttendee.firstName || ''} ${primaryAttendee.lastName || ''}`.trim(),
                contactId: primaryAttendee.contactId || '',
              },
              attendeeEmails: isSessionEvent
                ? ''
                : [primaryAttendee.email, ...attendees.filter((a) => !a.isPrimary).map((a) => a.email)]
                    .filter(Boolean)
                    .join(', '),
              comments: m.description || '',
              location: m.location,
              created: m.created,
              formAnswers: m.formAnswers.formAnswers,
              attachments: m.attachments,
              calendarId: m.calendarId,
              meetingType: m.meetingType,
              hostNames: hostDetails.map((u) => u.name).filter(Boolean),
              hostUserIds: hostDetails.map((u) => (u as any).hostID).filter(Boolean),
              topic: m.topic,
              isSessionEventType: isSessionEvent,
              sessionTitle: m.sessionTitle || m.topic,
              sessionRegistrationUrl: m.sessionRegistrationUrl || '',
              maxAttendeeCount: String(m.maxAttendeeCount || '0'),
              registeredAttendeeCount: String(m.registeredAttendeeCount || 0),
              registrationCutoff: m.registrationCutoff || '',
              attendees: m.attendees,
            };

            return updatedAttendeeCount?.meetingId === m.id
              ? { ...baseItem, registeredAttendeeCount: String(Math.max(0, updatedAttendeeCount.count)) }
              : baseItem;
          }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.paginationSubscription?.unsubscribe();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openMeetingLink(url: string): void {
    this.analyticsService.trackEvent(POSTHOG_KEYS.MEETING_JOINED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
    const w = openSecureNewTab(url);
    w?.focus?.();
  }

  openAttendeeListDialog(meeting: CalenderViewDataSource): void {
    const dialogRef = this.dialog.open(AttendeeListComponent, {
      data: {
        meetingId: meeting.meetingId,
        meetingTitle: meeting.topic || meeting.sessionTitle,
      },
      width: '900px',
      maxHeight: '80vh',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'attendee-list-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.confirmedAttendeesCount) {
          this.updatedAttendeeCount$$.next({ meetingId: meeting.meetingId, count: result.confirmedAttendeesCount });
        }
      });
  }

  generateEmbedCode(meeting: CalenderViewDataSource): string {
    const env = this.environmentService.getEnvironment();
    const hostUrl = env === Environment.PROD ? BOOOKING_URL_PROD : BOOOKING_URL_DEMO;
    return `<!-- bookmenow inline widget embed script begin -->
<div class="bookmenow-inline-widget" data-url="${meeting.sessionRegistrationUrl}" style="min-width:320px; height:710px;"></div>
<script type="text/javascript" src="${hostUrl}/assets/js/widget.js"></script>
<!-- bookmenow inline widget embed script end -->`;
  }

  handleCopyEmbedCodeClicked(): void {
    this.snackbarService.openSuccessSnack('MEETING_SCHEDULER.MEETING_LIST.EMBED_CODE_COPIED');
  }

  isSameDate(newDate: string, index: number): boolean {
    // Avioding multiple calls that cause previousDate have same value as newDate
    if (index === 0) {
      this.previousDate = newDate;
      return false;
    }

    if (this.previousDate === newDate) {
      return true;
    } else {
      this.previousDate = newDate;
      return false;
    }
  }

  filterByMeetingTypeIds(meetingTypeIds: string[]): void {
    this.selectedEventTypeFilter = meetingTypeIds[0];
    this.eventTypeFilter$$.next(meetingTypeIds);
  }

  openTeamMemberFilter(): void {
    this.teamMembers$.pipe(first()).subscribe({
      next: (members) => {
        const dialogRef = this.dialog.open(TeamMemberFilterDialogComponent, {
          width: '400px',
          data: {
            teamMembers: members,
            selectedUserIds: this.meetingSchedulerStoreService.getSelectedUserIds(),
            businessId: this.businessId,
            tab: this.selectedTab,
          } as TeamMemberFilterDialogData,
        });

        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe((result: string[] | undefined) => {
            if (result) {
              this.meetingSchedulerStoreService.setSelectedUserIds(result);
              this.analyticsService.trackEvent(
                POSTHOG_KEYS.TEAM_MEMBER_FILTER_APPLIED,
                POSTHOG_CATEGORIES.USER,
                POSTHOG_ACTIONS.CLICK,
                undefined,
                {
                  business_id: this.businessId,
                  selected_user_count: result.length,
                  is_all_users: result.length === members.length,
                  tab: this.selectedTab,
                },
              );
            }
          });
      },
      error: () => this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC'),
    });
  }

  navigateToContact(contactId: string): void {
    if (!contactId) {
      return;
    }
    this.router.navigateByUrl(`${this.contactProfilePageLink}${contactId}`);
  }

  stopPropagation(e: Event): void {
    // used to prevent event propagation for anchor tag
    e.stopPropagation();
  }

  trackByMeetingId(index: number, item: CalenderViewDataSource): string {
    return `${item.meetingId}-${item.date.start.getTime()}`;
  }

  onMeetingRescheduled(event: { id: string; newDate: Date }): void {
    const meetingIndex = this.allMeetings.findIndex((m) => m.id === event.id);

    if (meetingIndex !== -1) {
      const meeting = this.allMeetings[meetingIndex];
      const duration = meeting.end.getTime() - meeting.start.getTime();
      const newEnd = new Date(event.newDate.getTime() + duration);

      this.allMeetings[meetingIndex] = {
        ...meeting,
        start: event.newDate,
        end: newEnd,
      };

      this.allMeetings.sort((a, b) => a.start.getTime() - b.start.getTime());
      this.meetings$$.next([...this.allMeetings]);
      this.cdr.markForCheck();
    } else {
      this.reloadMeetings();
    }
  }

  private reloadMeetings(): void {
    this.meetingSchedulerStoreService.invalidateMeetingsCache();

    this.hasMore = false;
    this.nextCursor = '';
    this.isLoadingMore = false;
    this.allMeetings = [];
    this.meetings$$.next([]);
    this.hideLoader$.next(false);

    const filters: LoadMeetingFilters = {
      meetingTypeIds: this.eventTypeFilter$$.getValue(),
      timeSpan: this.meetingTimeSpanFilter$$.getValue(),
      userIds: this.meetingSchedulerStoreService.getSelectedUserIds(),
    };

    this.subscriptions.push(
      this.meetingSchedulerStoreService
        .loadPersonalMeetings({
          filters: {
            timeSpan: filters.timeSpan,
            meetingTypeIds: filters.meetingTypeIds && filters.meetingTypeIds.length > 0 ? filters.meetingTypeIds : [],
            ...(filters.userIds?.length ? { userIds: filters.userIds } : {}),
          },
          timeZone: { id: moment.tz.guess() },
          pageSize: 20,
          cursor: '',
          forceRefresh: true,
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.allMeetings = [...response.meetings];
            this.hasMore = response.hasMore;
            this.nextCursor = response.cursor;
            this.meetings$$.next([...response.meetings]);
            this.hideLoader$.next(true);
            this.cdr.markForCheck();
          },
          error: () => {
            this.hideLoader$.next(true);
            this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  changedMeetingListTab(value: 'upcoming' | 'past'): void {
    this.selectedTab = value;
    this.hideLoader$.next(false);
    this.previousDate = '';
    this.cdr.markForCheck();
    this.paginationSubscription?.unsubscribe();
    this.paginationSubscription = null;
    this.isLoadingMore = false;
    if (value === 'upcoming') {
      this.meetingTimeSpanFilter$$.next({ start: this.now, end: this.getTenYearsLater() });
    } else {
      this.timeRangeKeySelected = '90days';
      this.meetingTimeSpanFilter$$.next({ start: this.get90DaysPrevious(), end: this.now });
    }
  }

  setDateRange(range?: [Date, Date]): void {
    if (this.selectedTab === 'past' && range) {
      const start = moment(range[0]);
      let end = moment(range[1]);
      const now = moment();
      const endsToday = end.isSame(now, 'day');
      if (endsToday) {
        end.add(now.hour(), 'hour').add(now.minute(), 'minute').add(now.second(), 'second');
      } else {
        end = end.endOf('day');
      }

      const currentTimeSpan = this.meetingTimeSpanFilter$$.getValue();
      const newStartTime = start.toDate().getTime();
      const newEndTime = end.toDate().getTime();

      if (newStartTime !== currentTimeSpan.start.getTime() || newEndTime !== currentTimeSpan.end.getTime()) {
        this.meetingTimeSpanFilter$$.next({
          start: start.toDate(),
          end: end.toDate(),
        });
      }
    }
    if (this.timeRangeKeySelected === 'custom' && range) {
      this.timeRangeOptions.forEach((option) => {
        if (option.key === 'custom') {
          option.dateRange = range;
        }
      });
    }
  }

  setTimeRangeKey(key: string): void {
    this.timeRangeKeySelected = key;

    if (key !== 'custom') {
      const selectedOption = this.timeRangeOptions.find((option) => option.key === key);
      if (selectedOption && selectedOption.dateRange) {
        const [start, end] = selectedOption.dateRange;
        this.meetingTimeSpanFilter$$.next({ start, end });
      }
    }
  }

  private getTenYearsLater(): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 10);
    return date;
  }

  private get90DaysPrevious(): Date {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return ninetyDaysAgo;
  }

  private loadEventTypes(): void {
    this.personalEventTypes$ = this.meetingSchedulerStoreService
      .loadPersonalMeetingTypes({ returnCache: false })
      .pipe(
        map((meetingTypes) =>
          meetingTypes ? meetingTypes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })) : null,
        ),
      );
    this.teamEventTypeMeetings$ = this.meetingSchedulerStoreService.loadGroupMeetingTypes({ returnCache: true });
    this.teamEventTypes$ = this.teamEventTypeMeetings$.pipe(map(meetingTypeMapToArray));
    this.allMeetingTypes$ = combineLatest([this.personalEventTypes$, this.teamEventTypes$]).pipe(
      map(([personalMeetings, teamMeetings]) => sortAndJoinMeetingsTypes(personalMeetings, teamMeetings)),
      shareReplay(1),
    );
  }

  private setupLoadMeetingFilters(): Observable<LoadMeetingFilters> {
    const meetingTypeFilter$ = this.eventTypeFilter$$.pipe(distinctUntilChanged());
    const timeSpanFilter$ = this.meetingTimeSpanFilter$$.pipe(
      distinctUntilChanged(
        (a: TimeSpan, b: TimeSpan) => a.start.getTime() === b.start.getTime() && a.end.getTime() === b.end.getTime(),
      ),
    );

    if (this.isBusinessCenter) {
      return this.featureTeamMemberFilterEnabled$.pipe(
        startWith(false),
        distinctUntilChanged(),
        switchMap((enabled) => {
          if (enabled) {
            return this.userId$.pipe(
              filter((uid) => !!uid),
              first(),
              switchMap((userId) => {
                this.meetingSchedulerStoreService.initSelectedUserIdsIfNeeded(userId);
                const userIdsFilter$ = this.meetingSchedulerStoreService
                  .getSelectedUserIds$()
                  .pipe(distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])));
                return combineLatest([timeSpanFilter$, userIdsFilter$]).pipe(
                  map(([timeSpan, userIds]) => ({ meetingTypeIds: [], timeSpan, userIds })),
                );
              }),
            );
          }
          return combineLatest([meetingTypeFilter$, timeSpanFilter$]).pipe(
            map(([meetingTypeIds, timeSpan]) => ({ meetingTypeIds, timeSpan })),
          );
        }),
      );
    }

    return combineLatest([meetingTypeFilter$, timeSpanFilter$]).pipe(
      map(([meetingTypeIds, timeSpan]) => ({ meetingTypeIds, timeSpan })),
    );
  }

  private loadMeetings(
    filters: LoadMeetingFilters,
    cursor: string,
  ): Observable<{ meetings: Meeting[]; hasMore: boolean; cursor: string }> {
    const { meetingTypeIds, timeSpan } = filters;

    return this.meetingSchedulerStoreService
      .loadPersonalMeetings({
        filters: {
          timeSpan: timeSpan,
          meetingTypeIds: meetingTypeIds && meetingTypeIds.length > 0 ? meetingTypeIds : [],
          ...(filters.userIds?.length ? { userIds: filters.userIds } : {}),
        },
        timeZone: { id: moment.tz.guess() },
        pageSize: 20,
        cursor,
        forceRefresh: true,
      })
      .pipe(
        catchError((error) => {
          this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
          this.hideLoader$.next(true);
          throw error;
        }),
      );
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;
    const threshold = 100;

    if (scrollHeight - scrollPosition <= threshold && !this.isLoadingMore && this.hasMore) {
      this.loadMoreMeetings();
    }
  }

  loadMoreMeetings(): void {
    if (!this.hasMore || this.isLoadingMore) return;

    this.isLoadingMore = true;
    const filters: LoadMeetingFilters = {
      meetingTypeIds: this.eventTypeFilter$$.getValue(),
      timeSpan: this.meetingTimeSpanFilter$$.getValue(),
      userIds: this.meetingSchedulerStoreService.getSelectedUserIds(),
    };

    this.paginationSubscription?.unsubscribe();
    this.paginationSubscription = this.loadMeetings(filters, this.nextCursor).subscribe({
      next: (response) => {
        this.allMeetings = [...this.allMeetings, ...response.meetings];
        this.hasMore = response.hasMore;
        this.nextCursor = response.cursor;
        this.isLoadingMore = false;
        this.meetings$$.next([...this.allMeetings]);
      },
      error: () => {
        this.isLoadingMore = false;
        this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
      },
    });
  }
}

function meetingTypeMapToArray(meetingTypeMap: { [key: string]: MeetingType[] }): MeetingType[] {
  return Object.values(meetingTypeMap || {}).reduce((acc, types) => acc.concat(types), []);
}

function sortAndJoinMeetingsTypes(mts1: MeetingType[], mts2: MeetingType[]): MeetingType[] {
  return [...sortMeetingTypes(mts1), ...sortMeetingTypes(mts2)].map((mt) => ({
    ...mt,
    displayName: isGroupMeetingType(mt) ? `${mt.calendarName}: ${mt.name}` : mt.name,
  }));
}

function sortMeetingTypes(meetingTypes: MeetingType[]): MeetingType[] {
  return (meetingTypes || []).sort((mt1, mt2) => {
    const mt1Name = isGroupMeetingType(mt1) ? `${mt1.calendarName} ${mt1.name}` : mt1.name;
    const mt2Name = isGroupMeetingType(mt2) ? `${mt2.calendarName} ${mt2.name}` : mt2.name;
    return mt1Name.localeCompare(mt2Name, undefined, { numeric: true, sensitivity: 'base' });
  });
}
